import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Loader2 } from "lucide-react";
import { api, fileUrl, money } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function OrderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") || (params.get("provider") === "paypal" ? params.get("token") : null);
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("checking");
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return undefined;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        setOrder(data);
        if (data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) { clear(); cleared.current = true; }
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) { setState("failed"); return; }
      } catch {
        setState("error");
        return;
      }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000); else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 pt-20" data-testid="order-success-page">
      {state === "checking" && (
        <div className="py-32 text-center" data-testid="order-status-checking">
          <Loader2 className="mx-auto animate-spin text-white" />
          <p className="mt-6 text-zinc-400">Confirming your payment…</p>
        </div>
      )}
      {(state === "failed" || state === "error" || state === "pending") && (
        <div className="py-32 text-center" data-testid="order-status-problem">
          <h1 className="font-display text-3xl uppercase font-bold">
            {state === "pending" ? "Payment still processing" : "Payment not completed"}
          </h1>
          <p className="mt-4 text-zinc-400">
            {state === "pending" ? "We'll email you once it's confirmed." : "No charge was made. Your bag is still saved."}
          </p>
          <Link to="/checkout" className="btn-store mt-8">Back to checkout</Link>
        </div>
      )}
      {state === "paid" && order && (
        <div className="fade-up">
          <div className="w-14 h-14 rounded-full bg-[#ff2a2a] flex items-center justify-center"><Check className="text-white" /></div>
          <p className="eyebrow mt-8">Order #{order.number}</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tighter uppercase text-white mt-3" data-testid="order-confirmed-title">
            Thank you, {order.customer.name.split(" ")[0]}.
          </h1>
          <p className="mt-4 text-zinc-400" data-testid="order-confirmed-text">
            Your order is confirmed. A receipt is on its way to {order.customer.email}.
          </p>
          <div className="mt-12 border border-white/10 bg-[#0a0a0c] p-6 sm:p-8">
            <ul className="space-y-4">
              {order.items.map((i) => (
                <li key={`${i.product_id}-${i.color}`} className="flex gap-4 items-center">
                  <div className="w-14 h-16 bg-[#141416] flex-shrink-0">{i.photo && <img src={fileUrl(i.photo)} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="flex-1"><p className="text-sm text-white">{i.name}</p><p className="text-xs text-zinc-500">{i.color} × {i.quantity}</p></div>
                  <span className="text-sm">{money(i.price * i.quantity)}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-white/10 space-y-2 text-sm">
              <div className="flex justify-between text-zinc-400"><span>Subtotal</span><span className="text-white">{money(order.subtotal)}</span></div>
              {order.discount_amount > 0 && <div className="flex justify-between text-zinc-400" data-testid="order-discount"><span>Discount ({order.discount?.code || order.discount?.title})</span><span className="text-[#ff6b6b]">−{money(order.discount_amount)}</span></div>}
              <div className="flex justify-between text-zinc-400"><span>Shipping{order.shipping_discount > 0 ? ` (${order.discount?.code || order.discount?.title})` : ""}</span><span className="text-white">{order.shipping_total - (order.shipping_discount || 0) > 0 ? money(order.shipping_total - (order.shipping_discount || 0)) : "Free"}</span></div>
              {order.tax > 0 && <div className="flex justify-between text-zinc-400"><span>Tax</span><span className="text-white">{money(order.tax)}</span></div>}
              <div className="flex justify-between text-white font-medium pt-3 border-t border-white/10"><span>Total paid</span><span data-testid="order-total">{money(order.total)}</span></div>
            </div>
            <div className="mt-6 pt-5 border-t border-white/10 text-sm text-zinc-400">
              <p className="eyebrow mb-2">Shipping to</p>
              <p className="text-white">{order.customer.name}</p>
              <p>{order.shipping_address.line1}{order.shipping_address.line2 ? `, ${order.shipping_address.line2}` : ""}</p>
              <p>{order.shipping_address.city}{order.shipping_address.state ? `, ${order.shipping_address.state}` : ""} {order.shipping_address.postal_code}</p>
              <p>{order.shipping_address.country}</p>
            </div>
          </div>
          <Link to="/shop" className="btn-ghost-store mt-10" data-testid="order-continue-shopping">Continue shopping</Link>
        </div>
      )}
    </div>
  );
}
